// Tipos de autenticação e autorização

export interface User {
  id: string;
  email: string;
  nome: string;
  avatarUrl?: string;
  roles: UserRole[];
  wrikeId?: string;
  createdAt: Date;
  lastLogin: Date;
}

export enum UserRole {
  ADMIN = "ADMIN",
  /** Faz tudo o que o Admin faz, menos gerenciar usuários e permissões. */
  EDITOR = "EDITOR",
  GERENTE = "GERENTE",
  OPERADOR = "OPERADOR",
  VISUALIZADOR = "VISUALIZADOR",
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithWrike: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  processWrikeCallback: (code: string) => Promise<boolean>;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface WrikeAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
}

export interface WrikeUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
}

export interface AuthToken {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

// Permissões granulares
export enum Permission {
  // Clientes
  CLIENTES_VIEW = "clientes:view",
  CLIENTES_CREATE = "clientes:create",
  CLIENTES_EDIT = "clientes:edit",
  CLIENTES_DELETE = "clientes:delete",

  // Fornecedores
  FORNECEDORES_VIEW = "fornecedores:view",
  FORNECEDORES_CREATE = "fornecedores:create",
  FORNECEDORES_EDIT = "fornecedores:edit",
  FORNECEDORES_DELETE = "fornecedores:delete",

  // Planilhas
  PLANILHAS_VIEW = "planilhas:view",
  PLANILHAS_CREATE = "planilhas:create",
  PLANILHAS_EDIT = "planilhas:edit",
  PLANILHAS_DELETE = "planilhas:delete",
  PLANILHAS_EXPORT = "planilhas:export",
  PLANILHAS_IMPORT = "planilhas:import",

  // Sistema
  USERS_MANAGE = "users:manage",
  SETTINGS_MANAGE = "settings:manage",
  REPORTS_VIEW = "reports:view",

  // Wrike
  WRIKE_INTEGRATE = "wrike:integrate",
  WRIKE_SYNC = "wrike:sync",
}

// Mapeamento de roles para permissões
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: Object.values(Permission),

  [UserRole.EDITOR]: Object.values(Permission).filter(
    (p) => p !== Permission.USERS_MANAGE,
  ),

  [UserRole.GERENTE]: [
    Permission.CLIENTES_VIEW,
    Permission.CLIENTES_CREATE,
    Permission.CLIENTES_EDIT,
    Permission.FORNECEDORES_VIEW,
    Permission.FORNECEDORES_CREATE,
    Permission.FORNECEDORES_EDIT,
    Permission.PLANILHAS_VIEW,
    Permission.PLANILHAS_CREATE,
    Permission.PLANILHAS_EDIT,
    Permission.PLANILHAS_EXPORT,
    Permission.PLANILHAS_IMPORT,
    Permission.REPORTS_VIEW,
    Permission.WRIKE_INTEGRATE,
    Permission.WRIKE_SYNC,
  ],

  [UserRole.OPERADOR]: [
    Permission.CLIENTES_VIEW,
    Permission.FORNECEDORES_VIEW,
    Permission.PLANILHAS_VIEW,
    Permission.PLANILHAS_CREATE,
    Permission.PLANILHAS_EDIT,
    Permission.PLANILHAS_EXPORT,
  ],

  // Consulta tudo, inclusive exportar Excel e FACITE, e não altera nada. Quem
  // garante é a API, que recusa qualquer alteração deste perfil; a tela só
  // esconde os botões.
  [UserRole.VISUALIZADOR]: [
    Permission.CLIENTES_VIEW,
    Permission.FORNECEDORES_VIEW,
    Permission.PLANILHAS_VIEW,
    Permission.PLANILHAS_EXPORT,
    Permission.REPORTS_VIEW,
  ],
};

/**
 * Perfil gravado na API -> roles da tela. A API tem três perfis: Admin, Editor
 * e Visualizador. O "User" antigo, e a ausência do campo, contam como Admin,
 * que é como todo mundo usava o sistema até aqui.
 */
export const rolesDoPerfil = (perfil?: string | null): UserRole[] => {
  const p = perfil?.trim().toLowerCase();
  if (p === "visualizador") return [UserRole.VISUALIZADOR];
  if (p === "editor") return [UserRole.EDITOR];
  return [UserRole.ADMIN];
};
